<template>
  <header class="sticky-header">
    <EnvIndicator />
    <div class="header-container">
      <div class="header-content d-flex align-items-center position-relative">
        <nav class="navigation">
          <div class="d-flex gap-5">
            <router-link
              :to="{ name: 'JobsList' }"
              class="d-flex flex-grow-1"
            >
              <img
                class="logo"
                :src="logo"
                alt="Sealed"
              />
            </router-link>
            <div class="desktop-search-button">
              <HeaderButton
                v-if="appStateStore.currentUser"
                :label="t('common_actions.search')"
                icon="search"
                @click="showOmniSearch = true"
              />
            </div>
          </div>

          <div
            v-if="appStateStore.currentUser"
            class="right-nav"
          >
            <HeaderButton
              v-for="item in navItems"
              :key="`${item.label}-menu-button`"
              v-bind="item"
            />
            <DropdownMenu
              v-bind="dropDownNavItems"
              iconOnly
            />
          </div>
          <div
            v-if="appStateStore.currentUser"
            class="menu-container"
          >
            <HeaderButton
              :label="t('common_actions.search')"
              icon="search"
              @click="showOmniSearch = true"
            />
            <span
              class="material-symbols-outlined text-light fw-bold"
              @click="appStateStore.toggleSidebar()"
            >
              menu
            </span>
          </div>
        </nav>
      </div>
    </div>
    <Sidebar
      :isOpen="appStateStore.sidebarOpen"
      @close="appStateStore.toggleSidebar()"
    >
      <ul class="sidebar-items">
        <template
          v-for="item in combinedNavItems"
          :key="`${item.label}-sidebar-item`"
        >
          <SidebarItem
            :label="item.label"
            :icon="item.icon"
            @click="
              () => {
                item.onClick();
                appStateStore.toggleSidebar();
              }
            "
          />
        </template>
      </ul>
    </Sidebar>
    <Teleport
      to="#modal-target"
      v-if="showOmniSearch && !isContractorView()"
    >
      <OmniSearch @close="showOmniSearch = false" />
    </Teleport>
    <Teleport
      to="#modal-target"
      v-if="showOmniSearch && isContractorView()"
    >
      <OmniSearchContractor @close="showOmniSearch = false" />
    </Teleport>
  </header>
</template>

<script setup lang="ts">
import logo from '@/assets/images/sealed-logo.svg';
import useAppStore from '@/store/appState';
import EnvIndicator from '@shared/components/EnvIndicator.vue';
import Sidebar from '@shared/components/Sidebar/Sidebar.vue';
import SidebarItem from '@shared/components/Sidebar/SidebarItem.vue';
import DropdownMenu from '@shared/components/SiteHeader/DropdownMenu.vue';
import HeaderButton from '@shared/components/SiteHeader/HeaderButton.vue';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { getCurrentUser } from 'vuefire';

import OmniSearch from './OmniSearch.vue';
import OmniSearchContractor from './OmniSearchContractor.vue';

const appStateStore = useAppStore();

const router = useRouter();
const { t } = useI18n();
const isLoggedIn = ref(false);
const showOmniSearch = ref(false);

onMounted(async () => {
  const currentUser = await getCurrentUser();
  isLoggedIn.value = !!currentUser;
});

const navItems = [
  {
    icon: 'view_list',
    label: t('jobs_list.nav_label'),
    onClick: () => router.push({ name: 'JobsList' })
  },
  {
    icon: 'handyman',
    label: t('contractors_list.nav_label'),
    onClick: () => router.push({ name: 'ContractorsList' })
  },
  {
    icon: 'home_repair_service',
    label: t('common_equipment.equipment'),
    onClick: () => router.push({ name: 'HeatPumps' })
  }
];

const secondaryNavItems = [
  {
    icon: 'add',
    label: t('common_actions.add_new_contractor'),
    onClick: () => router.push({ name: 'ContractorCreate' })
  },
  {
    icon: 'settings',
    label: t('common_programs.programs'),
    onClick: () => router.push({ name: 'ProgramsList' })
  },
  {
    icon: 'assignment',
    label: t('terms_and_conditions.title'),
    onClick: () => router.push({ name: 'TermsAndConditionsList' })
  },
  {
    icon: 'electric_bolt',
    label: t('energy_providers.title'),
    onClick: () => router.push({ name: 'EnergyProvidersList' })
  },
  {
    icon: 'ad',
    label: t('announcements.title'),
    onClick: () => router.push({ name: 'AnnouncementsList' })
  },

  {
    icon: 'explore',
    label: t('common_programs.program_explorer.title'),
    onClick: () => router.push({ name: 'ProgramExplorer' })
  },
  {
    icon: 'logout',
    label: t('common_actions.sign_out'),
    onClick: () => router.push({ name: 'logout' })
  }
];

const dropDownNavItems = {
  icon: 'more_vert',
  label: t('common_labels.menu'),
  menuItems: secondaryNavItems
};

const combinedNavItems = [...navItems, ...secondaryNavItems];

const isContractorView = () => {
  // Use regex to match any route that starts with 'Contractor'
  // e.g. 'ContractorCreate', 'ContractorEdit', 'ContractorView'
  if (!router.currentRoute.value.name) return false;
  const contractorRouteMatch = RegExp('Contractor').test(
    router.currentRoute.value.name.toString()
  );
  if (contractorRouteMatch) {
    return true;
  } else return false;
};

window.addEventListener('keydown', (e) => {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault();
    showOmniSearch.value = true;
  }
});
</script>

<style lang="scss" scoped>
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10001; //1 higher than popper component;
  margin-bottom: 1.5rem;
}

.header-container {
  padding: 1.5rem 0;
  @media screen and (min-width: 992px) {
    padding: 2rem 0;
  }
}

.header-content {
  padding: 0 1.75rem;
}

.logo {
  height: 20px;
  @media screen and (min-width: 992px) {
    width: 133px;
    height: 40px;
  }
}

.navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;

  .right-nav,
  .desktop-search-button {
    display: none;
    @media screen and (min-width: 992px) {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
  }
  .menu-container {
    display: flex;
    gap: 1rem;
    @media screen and (min-width: 992px) {
      display: none;
    }
  }
}
</style>
